import { ApiError } from '@api/api-client';
import { changeOwnPassword } from '@api/app-users-api';
import FieldError from '@components/field-error';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@hooks/use-auth';
import { type ChangeOwnPasswordFormData, changeOwnPasswordSchema } from '@validation/schemas';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import { sileo } from 'sileo';

/**
 * Where a holder of a reset credential lands, and the only way off it.
 *
 * The current password is asked for again even though it was just typed to sign
 * in: that keeps a password out of app state across the redirect, and lets the
 * same screen serve a voluntary change.
 */
export default function ChangePassword(): React.JSX.Element {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ChangeOwnPasswordFormData>({
    resolver: zodResolver(changeOwnPasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    mode: 'onBlur',
  });

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const wasReset = user.mustChangePassword;

  const onSubmit = async (data: ChangeOwnPasswordFormData): Promise<void> => {
    setIsSubmitting(true);
    try {
      await changeOwnPassword(data.currentPassword, data.newPassword);
      // The requirement is cleared server-side; mirror it so the guard lets go.
      login({ ...user, mustChangePassword: false });
      sileo.success({ title: 'Password updated' });
      navigate('/editions', { replace: true });
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Could not change the password';
      if (error instanceof ApiError && message.toLowerCase().includes('current password')) {
        setError('currentPassword', { message });
      } else {
        sileo.error({ title: message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__accent" />
        <div className="login-card__body">
          <div className="login-card__header">
            <div className="login-card__logo">
              <KeyRound size={22} />
            </div>
            <h1 className="login-card__title">
              {wasReset ? 'Your password was reset' : 'Change your password'}
            </h1>
            <p className="login-card__description">
              {wasReset
                ? 'An administrator reset your password, so the one you just used is temporary. Choose a password of your own to continue.'
                : 'Choose a new password for your account.'}
            </p>
          </div>

          {wasReset && (
            <div className="login-form__notice" role="status">
              <ShieldAlert size={16} />
              <span>Only you will know the password you set here.</span>
            </div>
          )}

          <form className="login-form" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
            <div className="form-group">
              <label htmlFor="current-password" className="form-label">
                {wasReset ? 'Temporary password *' : 'Current password *'}
              </label>
              <input
                id="current-password"
                type="password"
                autoComplete="current-password"
                className={`form-input${errors.currentPassword ? ' form-input--error' : ''}`}
                placeholder={wasReset ? 'The password you were sent' : 'Your current password'}
                {...register('currentPassword')}
              />
              <FieldError error={errors.currentPassword} />
            </div>

            <div className="form-group">
              <label htmlFor="new-password" className="form-label">
                New password *
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                className={`form-input${errors.newPassword ? ' form-input--error' : ''}`}
                placeholder="Choose a new password"
                {...register('newPassword')}
              />
              <FieldError error={errors.newPassword} />
              {!errors.newPassword && (
                <span className="form-hint">
                  Minimum 8 characters, 1 uppercase, 1 number, 1 special character
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirm-new-password" className="form-label">
                Confirm new password *
              </label>
              <input
                id="confirm-new-password"
                type="password"
                autoComplete="new-password"
                className={`form-input${errors.confirmPassword ? ' form-input--error' : ''}`}
                placeholder="Repeat the new password"
                {...register('confirmPassword')}
              />
              <FieldError error={errors.confirmPassword} />
            </div>

            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Set new password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
